import React, { Suspense } from "react"
import {
	Routes,
	Route
} from "react-router-dom"
import { useSelector } from "react-redux"
import withRouter from "helpers/withRouter"
import GoogleOAuthCallback from '../../containers/Authentication/screens/LogIn/GoogleOAuthCallback'
import AddExemption from '../../containers/Documents/ExemptionCertificates/AddExemption/AddExemption'
import {
	getIsManager,
	getIsTaxAgentMainStatus,
	isUserTaxAgentOrManager,
	getIsOMP,
} from "../../redux/selectors"
import LogIn from "containers/Authentication/screens/LogIn"
import Dashboard from "containers/Home/Dashboard/Dashboard"
import RouteChunkFallback from "../shared/RouteChunkFallback"
import {
	LazyAddons,
	LazyAddClient,
	LazyAddNewsItem,
	LazyAddStore,
	LazyAddTransactions,
	LazyCheckout,
	LazyClients,
	LazyCreateReturn,
	LazyCreateTaxReturn,
	LazyDac7Report,
	LazyDocumentTemplate,
	LazyDocuments,
	LazyDueDates,
	LazyNews,
	LazyNewsItem,
	LazyOrderRegistration,
	LazyReport1099,
	LazyReports,
	LazySalesTaxReturns,
	LazyStores,
	LazySubscription,
	LazyTaxSettings,
	LazyThreshold,
	LazyTransactions,
	LazyVATReturns,
} from "../shared/heavyRouteChunks"
import ForgotPassword from "containers/Authentication/screens/ForgotPassword"
import LogInAdmin from "containers/Authentication/screens/LogInAdmin"
import Activation from "containers/Authentication/screens/Activation"
import VATPayment from "containers/VATPayment/VATPayment"
import CodeConfirmation from "containers/CodeConfirmation"
import InvitationValidateGate from "containers/Authentication/screens/InvitationValidateGate/InvitationValidateGate"
import useIntegrateStripeIfNeeded from "../hooks/useIntegrateStripeIfNeeded"
import { getCoreRoutes } from '../shared/coreRoutes'
import { getCommonRoutes } from '../shared/commonRoutes'
import SmartRedirect from "components/SmartRedirect/SmartRedirect"

const RoutesComponent = ({
	isLogged,
	isLoggingIn
}) => {
	const state = useSelector((state) => state)
	const IS_TAX_AGENT_MAIN_STATUS = getIsTaxAgentMainStatus(state)
	const IS_MANAGER = getIsManager(state)
	const IS_TAX_AGENT_OR_MANAGER = isUserTaxAgentOrManager(state.user.user)
	const IS_SHOW_VAT_RETURNS_IN_MENU = state.user.user.vat_returns_show_in_menu
	const IS_SHOW_SALES_TAX_RETURNS_IN_MENU =
			state.user.user.sales_tax_returns_show_in_menu
	const IS_OMP = getIsOMP(state)
	const IS_DAC7_ENABLED = state.user.user.dac7
	const openProjectName = state.user.openProjectName

	useIntegrateStripeIfNeeded(isLogged)

	const commonRoutes = (
			<>
				{getCommonRoutes()}
				<Route
						path='/join/accept'
						element={<InvitationValidateGate />}
				/>
			</>
	)

	const loggedInRoutes = (
			<>
				{getCoreRoutes()}
				<Route
						path='/home'
						element={<Dashboard />}
				/>
				<Route
						path='/'
						element={<Dashboard />}
				/>
				<Route
						path='/home/news'
						element={<LazyNews />}
				/>
				<Route
						path='/home/news/:newsId'
						element={<LazyNewsItem />}
				/>
				{IS_MANAGER && (
						<Route
								path='/home/add-news'
								element={<LazyAddNewsItem />}
						/>
				)}
				<Route
						path='/home/due-dates'
						element={<LazyDueDates />}
				/>
				<Route
						path='/home/threshold'
						element={<LazyThreshold />}
				/>

				<Route
						path='/transactions'
						element={<LazyTransactions />}
				/>
				<Route
						path='/transactions/import'
						element={<LazyAddTransactions />}
				/>
				<Route
						path='/stores'
						element={<LazyStores openProjectName={openProjectName} />}
				/>
				<Route
						path='/stores/add'
						element={<LazyAddStore openProjectName={openProjectName} />}
				/>
				<Route
						path='/reports'
						element={<LazyReports />}
				/>
				<Route
						path='/settings'
						element={<LazyTaxSettings />}
				/>
				<Route
						path='/settings/order'
						element={<LazyOrderRegistration />}
				/>
				<Route
						path='/subscription'
						element={<LazySubscription />}
				/>
				<Route
						path='/subscription/checkout'
						element={<LazyCheckout />}
				/>
				<Route
						path='/addons'
						element={<LazyAddons />}
				/>
				{IS_SHOW_VAT_RETURNS_IN_MENU && (
						<>
							<Route
									path='/vat-returns'
									element={<LazyVATReturns />}
							/>
							<Route
									path='/vat-payment'
									element={<VATPayment />}
							/>
							<Route
									path='/vat-returns/create-return'
									element={<LazyCreateReturn />}
							/>
							<Route
									path='/vat-returns/create-return/:id/:countryId/:countryName/:period/:periodType/:year/:reason/:typeOfReport'
									element={<LazyCreateReturn />}
							/>
						</>
				)}
				{!IS_TAX_AGENT_MAIN_STATUS && IS_SHOW_SALES_TAX_RETURNS_IN_MENU && (
						<>
							<Route
									path='/tax-returns'
									element={<LazySalesTaxReturns />}
							/>
							<Route
									path='/tax-returns/create-return'
									element={<LazyCreateTaxReturn />}
							/>
						</>
				)}

				{IS_TAX_AGENT_OR_MANAGER && (
						<>
							<Route
									path='/clients'
									element={<LazyClients />}
							/>
							<Route
									path='/clients/add-client'
									element={<LazyAddClient />}
							/>
						</>
				)}

				<Route
						path='/documents'
						element={<LazyDocuments />}
				/>
				<Route
						path='/documents/:type/create'
						element={<LazyDocumentTemplate />}
				/>
				<Route
						path='/documents/exemption/add'
						element={<AddExemption />}
				/>

				{IS_OMP && IS_DAC7_ENABLED && (
						<Route
								path='/dac7-reporting'
								element={<LazyDac7Report />}
						/>
				)}
				{IS_OMP && IS_DAC7_ENABLED && (
						<Route
								path='/dac7-immovable-property'
								element={<LazyDac7Report isImmovableProperty={true} />}
						/>
				)}

				<Route
						path='/return-1099'
						element={<LazyReport1099 />}
				/>

				{/* Payment success routes */}
				<Route
						path='/payments/subscription_bin_success/'
						element={<SmartRedirect />}
				/>
				<Route
						path='/api/payments/subscription_bin_success/'
						element={<SmartRedirect />}
				/>

				<Route
						path='*'
						element={<SmartRedirect />}
				/>
			</>
	)

	const notLoggedInRoutes = (
			<>
				<Route
						path='/social/complete/google-oauth2/*'
						element={<GoogleOAuthCallback />}
				/>
				<Route
						path='/login'
						element={<LogIn />}
				/>
				<Route
						path='/manager'
						element={<LogInAdmin />}
				/>
				<Route
						path='/forgot'
						element={<ForgotPassword />}
				/>
				<Route
						path='/forgot/:code'
						element={<ForgotPassword />}
				/>
				<Route
						path='/activation/:code'
						element={<Activation />}
				/>
				<Route
						path='/confirm-2fa'
						element={<CodeConfirmation />}
				/>
				{isLoggingIn && <Route
						path='*'
						element={<SmartRedirect />}
				/>}
			</>
	)

	return (
			<Suspense fallback={<RouteChunkFallback />}>
				<Routes >
					{commonRoutes}
					{isLogged ? loggedInRoutes : notLoggedInRoutes}
				</Routes >
			</Suspense>
	)
}

export default withRouter(RoutesComponent)
